import { useEffect, useId, useRef, useState } from "react";
import { API_URL } from "@/lib/api";
import { getAttribution, track } from "@/lib/attribution";
import { SMS_CONSENT_TEXT } from "@/lib/consent";

const MAX_BYTES = 15 * 1024 * 1024;
const MAX_FILES = 12;
const utilities = ["SCE", "PG&E", "FPL", "Oncor", "Duke Energy", "Other"];
const guideFields = ["Total kWh", "Peak kW demand", "Rate schedule", "Service voltage"];
const guideCopy = {
  SCE: ["Look for usage in the electric detail section.", "Look for billed or maximum demand; multiple TOU periods may appear.", "Find the tariff/service rate, often on the detail page.", "Find delivery/service details; request voltage if omitted."],
  "PG&E": ["Look for electric usage details, including TOU buckets.", "Review demand line items for each billed period.", "Find the electric rate schedule on the electric details.", "Check service details or request confirmation from the utility."],
  FPL: ["Look for the energy usage summary.", "For demand-billed accounts, find billed demand in the charge details.", "Look for the rate code or service classification.", "Request service voltage if it is absent from the bill."],
  Oncor: ["Use the retail provider's usage/billing details.", "Demand may be in delivery charges; obtain Oncor data if missing.", "Identify both the retail supply plan and delivery tariff.", "Check delivery service details or ask the provider."],
  "Duke Energy": ["Locate current-period energy usage.", "Check demand and delivery charge details for your operating company.", "Find the applicable rate/service code; state and subsidiary matter.", "Confirm service voltage with the operating utility if absent."],
  Other: ["Find current-period energy usage in kWh.", "Find billed demand in kW, if applicable.", "Find the tariff or service classification.", "Check service details or ask the utility."],
};

const isPdf = (f) => /\.pdf$/i.test(f.name) && (!f.type || f.type === "application/pdf");
const isImage = (f) => /\.(jpe?g|png)$/i.test(f.name) && (!f.type || ["image/jpeg", "image/png"].includes(f.type));

export function validateBillFiles(files) {
  if (!files.length) return "Choose at least one utility bill (PDF, JPG or PNG).";
  if (files.length > MAX_FILES) return "Upload at most 12 files per request.";
  if (files.some((f) => f.size === 0 || f.size > MAX_BYTES)) return "Each file must be nonempty and no larger than 15 MB.";
  if (files.some((f) => !isPdf(f) && !isImage(f))) return "Choose PDF, JPG or PNG files only.";
  return null;
}

export function BillTargetGuide({ utility }) {
  const [active, setActive] = useState(0);
  const copy = guideCopy[utility] || guideCopy.Other;
  return (
    <section className="sbb-guide" aria-label="Interactive utility bill guide" data-testid="bill-guide">
      <h3>Find the four numbers that matter</h3>
      <p>Illustrative guide for {utility}. Actual layouts vary; these are not utility bill screenshots.</p>
      <div className="sbb-guide-grid">
        <div className="sbb-paper" aria-label="Illustrative bill sections">
          <div className="sbb-paper-head">UTILITY BILL · EXAMPLE</div>
          {guideFields.map((field, i) => (
            <button key={field} type="button" aria-pressed={active === i} className={active === i ? "sbb-guide-active" : ""} onClick={() => setActive(i)}>
              <span>{i + 1}. {field}</span><span>Locate →</span>
            </button>
          ))}
        </div>
        <div aria-live="polite">
          <strong>{guideFields[active]}</strong>
          <p>{copy[active]}</p>
          <small>A PDF usually summarizes demand. Full interval data may require a separate utility portal export.</small>
        </div>
      </div>
    </section>
  );
}

/** Real HTTP upload with progress; never returns a simulated successful receipt. */
function uploadAudit({ body, signal, onProgress }) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_URL}/bill-audits`);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded, e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch (err) {
          reject(new Error("Invalid receipt from the audit service."));
        }
      } else if (xhr.status === 429) reject(new Error("Too many requests. Please try again shortly."));
      else reject(new Error(`The server could not accept your audit (${xhr.status}). Please retry or call 310-881-5155.`));
    };
    xhr.onerror = () => reject(new Error("Upload failed — check your connection and retry, or call 310-881-5155."));
    xhr.onabort = () => reject(new Error("Upload canceled."));
    signal?.addEventListener("abort", () => xhr.abort());
    xhr.send(body);
  });
}

export default function BillAuditModal(props) {
  const id = useId();
  const dialog = useRef(null);
  const controller = useRef(null);
  const requestKey = useRef(0);
  const idemRef = useRef(null);
  const [step, setStep] = useState(1);
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState({});
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [roof, setRoof] = useState(String(props.roofSqFt));
  const [spend, setSpend] = useState(String(props.monthlySpend));
  const [utility, setUtility] = useState("FPL");
  const [bracket, setBracket] = useState("unknown");
  const [customTax, setCustomTax] = useState("");
  const [taxConfirmed, setTaxConfirmed] = useState(false);
  const [ownership, setOwnership] = useState("owner");
  const [financing, setFinancing] = useState("cash");
  const [storage, setStorage] = useState(true);
  const [interval, setIntervalData] = useState(false);
  const [consent, setConsent] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState("");
  const [receipt, setReceipt] = useState(null);
  const errorRef = useRef(null);
  const successRef = useRef(null);

  useEffect(() => {
    if (!props.open) return;
    setStep(1);
    setFiles(props.initialFiles || []);
    setPreviews({});
    setRoof(String(props.snapshot?.roofSqFt ?? props.roofSqFt ?? 30000));
    setSpend(String(props.snapshot?.monthlySpend ?? props.monthlySpend ?? 10000));
    setBracket(props.snapshot?.taxRate == null ? "unknown" : "custom");
    setCustomTax(props.snapshot?.taxRate == null ? "" : String(Math.round(props.snapshot.taxRate * 100)));
    setTaxConfirmed(false);
    setReceipt(null);
    setError("");
    setBusy(false);
    setProgress(null);
    idemRef.current = `audit-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    track("bill_upload_start");
    const previous = document.activeElement;
    dialog.current?.showModal();
    return () => {
      requestKey.current += 1;
      controller.current?.abort();
      dialog.current?.close();
      previous?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  useEffect(() => () => Object.values(previews).forEach((u) => URL.revokeObjectURL(u)), []); // eslint-disable-line

  useEffect(() => { if (error) errorRef.current?.focus(); }, [error]);
  useEffect(() => { if (receipt) successRef.current?.focus(); }, [receipt]);

  function addFiles(incoming, rejected) {
    if (rejected.length) { setError("Choose up to 12 PDF, JPG or PNG files, each no larger than 15 MB."); return; }
    setFiles((current) => {
      const next = [...current];
      const nextPreviews = { ...previews };
      incoming.forEach((f) => {
        if (!next.some((x) => x.name === f.name && x.size === f.size && x.lastModified === f.lastModified)) {
          next.push(f);
          nextPreviews[f.name + f.size] = isImage(f) ? URL.createObjectURL(f) : null;
        }
      });
      setPreviews(nextPreviews);
      return next;
    });
    setError("");
  }

  const taxPercent = bracket === "unknown" ? null : bracket === "custom" ? Number(customTax) : Number(bracket);
  const taxValid = bracket === "unknown" || ((bracket !== "custom" || customTax.trim() !== "") && Number.isFinite(taxPercent) && taxPercent >= 0 && taxPercent <= 60);
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const phoneDigits = phone.replace(/\D/g, "");
  const phoneValid = phoneDigits.length >= 10 && phoneDigits.length <= 15;
  const roofValid = Number.isFinite(Number(roof)) && Number(roof) >= 100;
  const spendValid = Number.isFinite(Number(spend)) && Number(spend) > 0;
  const fileError = files.length ? validateBillFiles(files) : null;

  function step1Missing() {
    const missing = [];
    if (fileError) missing.push(fileError);
    if (company.trim().length < 2) missing.push("your company name");
    if (!emailValid) missing.push("a valid work email");
    if (!phoneValid) missing.push("a valid phone number");
    if (address.trim().length < 10) missing.push("the full facility address");
    if (!consent) missing.push("the authorization checkbox below");
    return missing;
  }

  function missingList() {
    const missing = step1Missing();
    if (!roofValid) missing.push("a usable roof area (at least 100 sq ft)");
    if (!spendValid) missing.push("your average monthly power spend");
    if (!taxValid) missing.push("a tax rate choice (or pick “I'm not sure”)");
    return missing;
  }

  function continueToStep2() {
    if (busy) return;
    const missing = step1Missing();
    if (missing.length) {
      setError(missing.length === 1 ? `One more thing: add ${missing[0]}.` : `Almost there — please add: ${missing.slice(0, -1).join(", ")} and ${missing[missing.length - 1]}.`);
      return;
    }
    setError("");
    setStep(2);
  }

  async function submit(event) {
    event.preventDefault();
    if (busy) return;
    const missing = missingList();
    if (missing.length) {
      setError(missing.length === 1 ? `One more thing: add ${missing[0]}.` : `Almost there — please add: ${missing.slice(0, -1).join(", ")} and ${missing[missing.length - 1]}.`);
      return;
    }
    setBusy(true);
    setError("");
    const currentRequest = ++requestKey.current;
    controller.current = new AbortController();
    try {
      for (const file of files) {
        if (isPdf(file)) {
          const signature = new TextDecoder().decode(await file.slice(0, 5).arrayBuffer());
          if (signature !== "%PDF-") throw new Error("A selected file does not have a valid PDF header. Export a PDF from your utility.");
        }
      }
      const metadata = {
        company: company.trim(), email: email.trim(), phone: phone.trim(), address: address.trim(),
        roofSqFt: Number(roof), monthlySpend: Number(spend), utility,
        taxRate: taxPercent == null ? null : taxPercent / 100,
        taxRateConfirmed: taxPercent != null && taxConfirmed,
        ownership, financing, storageInterest: storage, intervalDataAvailable: interval,
        consentVersion: "audit-request-v1", calculatorSnapshot: props.snapshot || null,
        smsConsent: { granted: smsConsent, text: SMS_CONSENT_TEXT, recordedAt: new Date().toISOString() },
        attribution: { ...getAttribution(), source: "bill_upload", page: window.location.pathname },
      };
      const body = new FormData();
      body.append("metadata", JSON.stringify(metadata));
      files.forEach((file) => body.append("bills", file, file.name));
      const accepted = await uploadAudit({ body, signal: controller.current.signal, onProgress: (loaded, total) => currentRequest === requestKey.current && setProgress(Math.round((loaded / total) * 100)) });
      if (currentRequest !== requestKey.current) return;
      if (!accepted.auditId || !["received", "queued"].includes(accepted.status)) throw new Error("The server did not confirm receipt. Please retry.");
      setReceipt(accepted);
      setProgress(null);
      track("bill_upload_submit");
      props.onAccepted?.(accepted);
    } catch (e) {
      if (currentRequest !== requestKey.current || e.name === "AbortError") return;
      setError(e instanceof Error ? e.message : "Upload failed. Please retry or call 310-881-5155.");
    } finally {
      if (currentRequest === requestKey.current) setBusy(false);
    }
  }

  if (!props.open) return null;
  const field = (label, control) => <label className="sbb-field"><span>{label}</span>{control}</label>;
  return (
    <dialog ref={dialog} className="sbb-modal" aria-labelledby={`${id}-title`} data-testid="bill-audit-modal"
      onCancel={(event) => { event.preventDefault(); if (!busy) props.onClose(); }}>
      <div className="sbb-modal-top">
        <h2 id={`${id}-title`}>Upload your electric bill</h2>
        <button type="button" disabled={busy} onClick={props.onClose} aria-label="Close bill upload" data-testid="bill-modal-close">×</button>
      </div>
      {receipt ? (
        <section className="sbb-success" data-testid="bill-audit-success">
          <h3 tabIndex={-1} ref={successRef}>{receipt.blackBeltUnlocked ? "Solar Black Belt unlocked" : "Your bill is received"}</h3>
          <p>Audit reference: <strong>{receipt.auditId}</strong></p>
          <p>Your request is saved and in the review queue. The project team reviews it manually and will follow up by phone or email with next steps and a timeline. Keep this reference — it identifies your saved project.</p>
          <p>The Solar Black Belt badge recognizes this platform milestone. It is not a professional credential.</p>
          <button type="button" className="sbb-primary" onClick={props.onClose}>Return to my dashboard</button>
        </section>
      ) : (
        <form onSubmit={submit} data-testid="bill-audit-form">
          <div className="sbb-steps" data-testid="bill-steps" aria-hidden="true">
            <span className={step === 1 ? "on" : ""}>1 · Bill &amp; contact</span>
            <span className={step === 2 ? "on" : ""}>2 · Fine-tune (optional)</span>
          </div>
          {step === 1 ? (
            <>
              <p>Upload a recent bill — or up to 12 files. PDF, JPG or PNG, including clear phone photos. You may redact account and payment numbers. In a hurry? Send the form without files and we'll follow up for the bill.</p>
              <fieldset disabled={busy}>
                <legend className="sbb-sr">Bill and contact details</legend>
                <div className="sbb-drop" role="button" tabIndex={0} aria-label="Choose or drop utility bill files (PDF, JPG or PNG)"
                  onClick={() => document.getElementById(`${id}-file`)?.click()}
                  onKeyDown={(e) => { if (e.key === "Enter") document.getElementById(`${id}-file`)?.click(); }}>
                  <strong>Drop bill files or click to choose</strong>
                  <span>1–12 files · PDF, JPG or PNG · 15 MB each · No payment required</span>
                  <input id={`${id}-file`} type="file" accept=".pdf,.jpg,.jpeg,.png" multiple style={{ display: "none" }} onChange={(e) => { addFiles(Array.from(e.target.files || []), []); e.target.value = ""; }} />
                </div>
                <ul className="sbb-files" data-testid="bill-file-list">
                  {files.map((file, i) => (
                    <li key={`${file.name}-${file.size}-${i}`}>
                      <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        {previews[file.name + file.size] ? (
                          <img className="sbb-thumb" src={previews[file.name + file.size]} alt={`Preview of ${file.name}`} />
                        ) : (
                          <span className="sbb-filechip" aria-hidden="true">PDF</span>
                        )}
                        {file.name} · {(file.size / 1048576).toFixed(1)} MB
                      </span>
                      <button type="button" onClick={() => setFiles((current) => current.filter((_, index) => index !== i))} aria-label={`Remove ${file.name}`}>
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
                {fileError && <p className="sbb-hint">{fileError}</p>}
                {progress != null && busy && (
                  <div className="sbb-progress" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100} data-testid="upload-progress">
                    <i style={{ width: `${progress}%` }} />
                  </div>
                )}
                {busy && progress == null && <p className="sbb-hint">Uploading…</p>}
                <div className="sbb-form-grid">
                  {field("Company", <input required minLength={2} maxLength={160} autoComplete="organization" data-testid="bill-company" value={company} onChange={(e) => setCompany(e.target.value)} />)}
                  {field("Work email", <input required type="email" maxLength={254} autoComplete="email" data-testid="bill-email" value={email} aria-invalid={email.length > 0 && !emailValid} onChange={(e) => setEmail(e.target.value)} />)}
                  {field("Phone", <input required type="tel" maxLength={30} autoComplete="tel" inputMode="tel" data-testid="bill-phone" value={phone} aria-invalid={phone.length > 0 && !phoneValid} onChange={(e) => setPhone(e.target.value)} />)}
                  {field("Facility address", <input required minLength={10} maxLength={300} autoComplete="street-address" data-testid="bill-address" value={address} onChange={(e) => setAddress(e.target.value)} />)}
                </div>
                <label className="sbb-check"><input required type="checkbox" data-testid="bill-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)} />I am authorized to share these bills and request contact about this audit under the <a href="/privacy" target="_blank" rel="noreferrer">privacy policy</a>. Marketing messages require a separate opt-in.</label>
                <label className="sbb-check" data-testid="bill-sms-consent-row"><input type="checkbox" data-testid="bill-sms-consent" checked={smsConsent} onChange={(e) => setSmsConsent(e.target.checked)} />{SMS_CONSENT_TEXT}</label>
              </fieldset>
              {error && <p className="sbb-error" role="alert" tabIndex={-1} ref={errorRef} data-testid="bill-audit-error">{error}</p>}
              <div className="sbb-modal-actions">
                <a href="tel:+13108815155">310-881-5155</a>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", justifyContent: "flex-end" }}>
                  <button type="button" className="sbb-quiet" onClick={submit} disabled={busy} data-testid="bill-skip">Skip — send now</button>
                  <button type="button" className="sbb-primary" onClick={continueToStep2} disabled={busy} data-testid="bill-continue">Continue →</button>
                </div>
              </div>
              <p className="sbb-hint">Uploading starts a manual review by the project team — it does not produce an automated bill analysis. Step 2 adds optional details that sharpen the review; you can skip it.</p>
            </>
          ) : (
            <>
              <p><strong>Optional fine-tuning.</strong> Prefilled with typical values{props.snapshot ? " from your calculator session" : ""}. Edit anything — or send it as is and we'll confirm during your review.</p>
              <fieldset disabled={busy}>
                <legend className="sbb-sr">Optional facility and financial details</legend>
                <div className="sbb-form-grid">
                  {field("Usable roof area (square feet)", <input required type="number" min={100} max={100000000} inputMode="numeric" data-testid="bill-roof" value={roof} aria-invalid={!roofValid} onChange={(e) => setRoof(e.target.value)} />)}
                  {field("Average monthly power spend ($)", <input required type="number" min={1} max={10000000} inputMode="numeric" data-testid="bill-spend" value={spend} aria-invalid={!spendValid} onChange={(e) => setSpend(e.target.value)} />)}
                  {field("Utility / delivery company", <select data-testid="bill-utility" value={utility} onChange={(e) => setUtility(e.target.value)}>{utilities.map((u) => <option key={u}>{u}</option>)}</select>)}
                  {field("Marginal business tax rate", <select data-testid="bill-bracket" value={bracket} onChange={(e) => { setBracket(e.target.value); setTaxConfirmed(false); }}>
                    <option value="unknown">I'm not sure — ask my tax adviser</option><option value="0">0% / tax-exempt</option>
                    <option value="21">21% federal corporate illustration</option><option value="24">24% pass-through illustration</option>
                    <option value="32">32% pass-through illustration</option><option value="37">37% pass-through illustration</option>
                    <option value="custom">Custom effective marginal rate</option>
                  </select>)}
                  {bracket === "custom" && field("Custom marginal tax rate (%)", <input required type="number" step="0.01" min={0} max={60} inputMode="decimal" data-testid="bill-custom-tax" value={customTax} aria-invalid={!taxValid} onChange={(e) => setCustomTax(e.target.value)} />)}
                  {field("Facility control", <select data-testid="bill-ownership" value={ownership} onChange={(e) => setOwnership(e.target.value)}>
                    <option value="owner">Property owner</option><option value="tenant">Tenant — permission needs review</option>
                  </select>)}
                  {field("Capital preference", <select data-testid="bill-financing" value={financing} onChange={(e) => setFinancing(e.target.value)}>
                    <option value="cash">Own the system / compare options</option><option value="cpace">Explore C-PACE</option><option value="ppa">Explore a PPA</option>
                  </select>)}
                </div>
                <p className="sbb-hint">Federal and state rates are not simply additive. Your tax adviser must confirm the effective rate, eligibility, ownership, and ability to use deductions or credits.</p>
                <label className="sbb-check"><input type="checkbox" checked={taxConfirmed} disabled={bracket === "unknown"} onChange={(e) => setTaxConfirmed(e.target.checked)} />My tax adviser has confirmed the entered marginal rate.</label>
                <label className="sbb-check"><input type="checkbox" checked={storage} onChange={(e) => setStorage(e.target.checked)} />Compare solar with Tesla storage or a suitable commercial BESS.</label>
                <label className="sbb-check"><input type="checkbox" checked={interval} onChange={(e) => setIntervalData(e.target.checked)} />I can provide a utility interval-data export separately.</label>
                <details className="sbb-guidewrap">
                  <summary>How to read your bill (optional guide)</summary>
                  <BillTargetGuide utility={utility} />
                </details>
              </fieldset>
              {error && <p className="sbb-error" role="alert" tabIndex={-1} ref={errorRef} data-testid="bill-audit-error">{error}</p>}
              <div className="sbb-modal-actions">
                <button type="button" className="sbb-quiet" onClick={() => { setStep(1); setError(""); }} disabled={busy} data-testid="bill-back">← Back</button>
                <button className="sbb-primary" type="submit" disabled={busy} data-testid="bill-audit-submit">{busy ? (progress != null ? `Uploading ${progress}%…` : "Sending securely…") : "Send details & request my review"}</button>
              </div>
              <p className="sbb-hint">A bill does not establish tax eligibility, engineering feasibility, financing approval, or guaranteed savings. Duplicate submissions with the same request are combined automatically.</p>
            </>
          )}
        </form>
      )}
    </dialog>
  );
}
